import { createFeature, createReducer, on } from '@ngrx/store';
import {
  submitUsername,
  submitUsernameSuccess,
  submitUsernameFailure,
  verifyOtp,
  verifyOtpSuccess,
  verifyOtpFailure,
  resendOtp,
  resendOtpSuccess,
  resendOtpFailure,
  setPassword,
  setPasswordSuccess,
  setPasswordFailure,
  complete,
  reset,
  setLoading,
  setError,
} from './password-recovery.actions';

export interface PasswordRecoveryState {
  username: string | null;
  activationToken: string | null;
  tokenType: string | null;
  nextStep: string | null;
  loginMethod: string | null;
  expiresIn: number | null;
  expiresAt: string | null;
  message: string | null;
  resultStatus: string | null;
  canGoToStep: number;
  loading: boolean;
  error: string | null;
}

export const initialState: PasswordRecoveryState = {
  username: null,
  activationToken: null,
  tokenType: null,
  nextStep: null,
  loginMethod: null,
  expiresIn: null,
  expiresAt: null,
  message: null,
  resultStatus: null,
  canGoToStep: 1,
  loading: false,
  error: null,
};

export const passwordRecoveryFeature = createFeature({
  name: 'passwordRecovery',
  reducer: createReducer(
    initialState,
    // Submitting a number starts a new recovery: nothing an abandoned one left
    // behind — above all its bearer — may carry into it (F-047).
    on(submitUsername, () => ({
      ...initialState,
      loading: true,
    })),
    // Written whole, never merged: a field the response omits is null, not
    // the previous attempt's value.
    on(submitUsernameSuccess, (state, payload) => ({
      ...state,
      username: payload.username,
      activationToken: payload.activationToken ?? null,
      tokenType: payload.tokenType ?? null,
      nextStep: payload.nextStep ?? null,
      loginMethod: payload.loginMethod ?? null,
      expiresIn: payload.expiresIn ?? null,
      expiresAt: payload.expiresAt ?? null,
      message: payload.message ?? null,
      canGoToStep: 2,
      loading: false,
      error: null,
    })),
    on(submitUsernameFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error,
    })),
    on(verifyOtp, state => ({
      ...state,
      loading: true,
      error: null,
    })),
    on(verifyOtpSuccess, (state, payload) => ({
      ...state,
      activationToken: payload.activationToken ?? state.activationToken,
      tokenType: payload.tokenType ?? state.tokenType,
      nextStep: payload.nextStep ?? state.nextStep,
      loginMethod: payload.loginMethod ?? state.loginMethod,
      expiresIn: payload.expiresIn ?? state.expiresIn,
      expiresAt: payload.expiresAt ?? state.expiresAt,
      message: payload.message ?? state.message,
      canGoToStep: Math.max(state.canGoToStep, 3),
      loading: false,
      error: null,
    })),
    on(verifyOtpFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error,
    })),
    on(resendOtp, state => ({
      ...state,
      loading: true,
      error: null,
    })),
    on(resendOtpSuccess, (state, payload) => ({
      ...state,
      activationToken: payload.activationToken ?? state.activationToken,
      tokenType: payload.tokenType ?? state.tokenType,
      nextStep: payload.nextStep ?? state.nextStep,
      loginMethod: payload.loginMethod ?? state.loginMethod,
      expiresIn: payload.expiresIn ?? state.expiresIn,
      expiresAt: payload.expiresAt ?? state.expiresAt,
      message: payload.message ?? state.message,
      canGoToStep: Math.max(state.canGoToStep, 2),
      loading: false,
      error: null,
    })),
    on(resendOtpFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error,
    })),
    on(setPassword, state => ({
      ...state,
      loading: true,
      error: null,
    })),
    on(setPasswordSuccess, (state, payload) => ({
      ...state,
      message: payload.message ?? state.message,
      resultStatus: payload.resultStatus,
      canGoToStep: Math.max(state.canGoToStep, 4),
      loading: false,
      error: null,
    })),
    on(setPasswordFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error,
    })),
    on(complete, () => initialState),
    on(reset, () => initialState),
    on(setLoading, (state, { loading }) => ({
      ...state,
      loading,
      error: loading ? null : state.error,
    })),
    on(setError, (state, { error }) => ({
      ...state,
      error,
      loading: false,
    }))
  ),
});
